/**
 * Team-invite client helpers.
 *
 * The invite email links to `/auth?team_invite=<token>`. Mirroring the
 * carrier-invite mechanism (`cr-invite-token` in Signup.tsx/AuthCallback.tsx),
 * the token is stashed in localStorage at the first page the invitee lands on
 * so it survives the email-verification / OAuth round-trip, then consumed
 * after sign-in by calling the vernot `team-invite` edge function's `accept`
 * action. The server validates everything (token, expiry, email match, role
 * from the invite row) — these helpers only ferry the token.
 */
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

const TOKEN_KEY = "cr-team-invite-token";
const EXPIRY_KEY = "cr-team-invite-token-expiry";
// Same client-side TTL as the carrier invite stash. The server enforces the
// real 14-day invite expiry; this only bounds how long a stale browser keeps
// retrying an old token.
const TOKEN_TTL_MS = 24 * 60 * 60 * 1000;

/** Stash a team-invite token (from the `team_invite` URL param) so it
 *  survives signup/OAuth redirects. No-op when the param is absent. */
export function stashTeamInviteTokenFromUrl(search: string): void {
  try {
    const token = new URLSearchParams(search).get("team_invite");
    if (!token) return;
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(EXPIRY_KEY, String(Date.now() + TOKEN_TTL_MS));
  } catch {
    // localStorage unavailable (private mode edge cases) — the user can still
    // re-open the invite link after signing in.
  }
}

/** Extract the `{ error, code }` body (and HTTP status) supabase-js hides
 *  inside a FunctionsHttpError, falling back to the generic message. The
 *  status lets callers distinguish "already gone" (404) / "already pending"
 *  (409) from real failures even when the server omits a `code`. */
export async function parseFunctionsError(
  error: unknown,
): Promise<{ message: string; code?: string; status?: number }> {
  let message = error instanceof Error ? error.message : "Request failed";
  let code: string | undefined;
  let status: number | undefined;
  if (error instanceof FunctionsHttpError && error.context) {
    status = typeof error.context.status === "number" ? error.context.status : undefined;
    try {
      const body = await error.context.json();
      if (body && typeof body === "object") {
        if (typeof body.error === "string" && body.error) message = body.error;
        if (typeof body.code === "string") code = body.code;
      }
    } catch {
      // non-JSON body — keep the generic message
    }
  }
  return { message, code, status };
}

export type TeamInviteAcceptResult =
  | { status: "none" }
  | { status: "accepted"; companyId?: string; role?: string }
  | { status: "error"; message: string; code?: string };

/**
 * Is this accept failure a benign, terminal invite state rather than a real
 * error? The accept is idempotent — if a stashed token points at an invite
 * that's already been accepted (e.g. the invitee already joined), or that's
 * expired / cancelled / no longer exists, there's nothing for the now-signed-in
 * user to act on and nothing went wrong. We treat these as "none" so callers
 * don't show a scary "Could not accept the team invite" toast. Only genuine
 * failures (bad request, server error, role-attach failure) surface as errors.
 */
function isBenignInviteState({
  message,
  code,
  status,
}: {
  message: string;
  code?: string;
  status?: number;
}): boolean {
  // Gone / not found → the invite no longer exists or is already consumed.
  if (status === 404 || status === 410) return true;
  const c = (code ?? "").toLowerCase();
  if (
    [
      "already_accepted",
      "accepted",
      "invite_accepted",
      "already_member",
      "already_joined",
      "not_found",
      "invite_not_found",
      "expired",
      "invite_expired",
      "cancelled",
      "canceled",
      "invite_cancelled",
      "invalid_invite",
      "invite_invalid",
    ].includes(c)
  ) {
    return true;
  }
  const m = (message ?? "").toLowerCase();
  return /no longer valid|already (been )?accepted|already (a )?(member|joined)|has expired|expired|cancell?ed/.test(
    m,
  );
}

/**
 * Consume any stashed team-invite token and accept it as the now-signed-in
 * user. Always clears the stash (one attempt per stashed token — the server
 * accept is idempotent, and a failed token shouldn't wedge future logins).
 */
export async function acceptPendingTeamInvite(): Promise<TeamInviteAcceptResult> {
  let token: string | null = null;
  let expiry: string | null = null;
  try {
    token = localStorage.getItem(TOKEN_KEY);
    expiry = localStorage.getItem(EXPIRY_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRY_KEY);
  } catch {
    return { status: "none" };
  }
  if (!token) return { status: "none" };
  if (expiry && Date.now() > Number(expiry)) return { status: "none" };

  const { data, error } = await supabase.functions.invoke("team-invite", {
    body: { action: "accept", token },
  });
  if (error) {
    const parsed = await parseFunctionsError(error);
    // An already-accepted / expired / cancelled / missing invite is a benign
    // terminal state, not an error — the accept is idempotent and the token was
    // already cleared above. Surfacing it as a toast confuses the inviter (whose
    // browser may have stashed the token), making a successfully-sent invite look
    // broken. Swallow these; only real failures become an error result.
    if (isBenignInviteState(parsed)) {
      return { status: "none" };
    }
    return { status: "error", message: parsed.message, code: parsed.code };
  }
  return {
    status: "accepted",
    companyId: typeof data?.company_id === "string" ? data.company_id : undefined,
    role: typeof data?.role === "string" ? data.role : undefined,
  };
}
